import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { deferAfterPaint } from '../lib/deferAfterPaint';
import { playNotificationSound } from '../lib/notificationSound';
import {
  imageQueueActionCount,
  type ImageQueueStats,
} from './useCatalogImageResolveQueue';
import type { ImageResolveJobPublic } from './useCatalogImageResolveJobDetail';

export type ImageResolveJobStatus = ImageResolveJobPublic['status'];

const POLL_MS = 2000;
const QUEUE_STATS_MS = 30_000;
const QUEUE_DISMISS_KEY = 'hamssah.image-queue-card.dismissed';

/** Counts fingerprint — dismissal only holds while the queue stays identical. */
function queueSignature(stats: ImageQueueStats | null): string | null {
  if (!stats) return null;
  return `${stats.review ?? 0}-${stats.pending ?? 0}-${stats.failed ?? 0}`;
}

function readDismissedSignature(): string | null {
  try {
    return localStorage.getItem(QUEUE_DISMISS_KEY);
  } catch {
    return null;
  }
}

function isTerminal(status: ImageResolveJobStatus) {
  return status === 'COMPLETED' || status === 'FAILED';
}

function isActive(status: ImageResolveJobStatus) {
  return status === 'PENDING' || status === 'RUNNING';
}

/** Polls active PIIS resolve batch for inbox progress (survives navigation). */
export function useCatalogImageResolveJobInbox() {
  const token = getToken();
  const { showToast } = useToast();
  const [job, setJob] = useState<ImageResolveJobPublic | null>(null);
  const [queueStats, setQueueStats] = useState<ImageQueueStats | null>(null);
  const [dismissedQueueSig, setDismissedQueueSig] = useState<string | null>(readDismissedSignature);
  const [err, setErr] = useState<string | null>(null);
  const notifiedRef = useRef<Set<string>>(new Set());
  const pollEnabledRef = useRef(true);

  const reloadQueueStats = useCallback(async () => {
    if (!token) return;
    try {
      const stats = await apiGet<ImageQueueStats>('/catalog/admin/image-intelligence/stats', token);
      setQueueStats(stats);
    } catch {
      // ponytail: stats are advisory for inbox card — job poll owns hard errors
    }
  }, [token]);

  const dismissResult = useCallback(() => {
    setJob(null);
    setErr(null);
    window.dispatchEvent(new Event('wapharma-inbox-reload'));
  }, []);

  /** Hide the queue card until its counts change (new items resurface it). */
  const dismissQueueCard = useCallback(() => {
    const sig = queueSignature(queueStats);
    if (!sig) return;
    setDismissedQueueSig(sig);
    try {
      localStorage.setItem(QUEUE_DISMISS_KEY, sig);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event('wapharma-inbox-reload'));
  }, [queueStats]);

  useEffect(() => {
    if (!token) {
      setJob(null);
      setQueueStats(null);
      return;
    }

    let statsCancelled = false;
    let statsTimer: ReturnType<typeof setInterval> | null = null;

    const loadStats = async () => {
      if (statsCancelled || document.hidden) return;
      await reloadQueueStats();
    };

    void loadStats();
    statsTimer = setInterval(() => void loadStats(), QUEUE_STATS_MS);

    const onQueueReload = () => void loadStats();
    window.addEventListener('wapharma-image-queue-reload', onQueueReload);

    return () => {
      statsCancelled = true;
      if (statsTimer) clearInterval(statsTimer);
      window.removeEventListener('wapharma-image-queue-reload', onQueueReload);
    };
  }, [reloadQueueStats, token]);

  useEffect(() => {
    if (!token) {
      setJob(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startPolling = () => {
      if (timer || document.hidden) return;
      timer = setInterval(() => void poll(), POLL_MS);
    };

    const poll = async () => {
      if (!pollEnabledRef.current) return;
      try {
        const raw = await apiGet<ImageResolveJobPublic | null>(
          '/catalog/admin/image-intelligence/jobs/active',
          token,
        );
        const active = unwrapData(raw) as ImageResolveJobPublic | null;
        if (cancelled) return;

        if (active && isActive(active.status)) {
          setJob(active);
          setErr(null);
          startPolling();
          return;
        }

        if (active && isTerminal(active.status)) {
          setJob(active);
          setErr(null);
          stopPolling();
          if (!notifiedRef.current.has(active.jobId)) {
            notifiedRef.current.add(active.jobId);
            showToast(
              `Images terminées — ${active.readyCount.toLocaleString('fr-FR')} prêtes, ${active.reviewCount.toLocaleString('fr-FR')} à valider.`,
              { tone: 'success' },
            );
            playNotificationSound();
            window.dispatchEvent(new Event('wapharma-inbox-reload'));
          }
          return;
        }

        setJob((prev) => (prev && isTerminal(prev.status) ? prev : null));
        stopPolling();
      } catch (e: unknown) {
        if (cancelled) return;
        setErr(getDisplayError(e));
        setJob(null);
        stopPolling();
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }
      if (!pollEnabledRef.current) return;
      void poll();
    };

    const cancelDefer = deferAfterPaint(() => {
      if (!cancelled && pollEnabledRef.current) void poll();
    });

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      cancelDefer();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [showToast, token]);

  const inFlight = Boolean(job && isActive(job.status));
  const showResult = Boolean(job && job.status === 'COMPLETED');
  const queueActionCount = imageQueueActionCount(queueStats);
  const showQueueCard =
    queueActionCount > 0 && queueSignature(queueStats) !== dismissedQueueSig;

  return {
    job,
    queueStats,
    queueActionCount,
    reloadQueueStats,
    err,
    inFlight,
    showResult,
    showQueueCard,
    dismissResult,
    dismissQueueCard,
    imageResolveProgress: job
      ? { current: job.processedProducts, total: job.totalProducts || 1 }
      : null,
    inboxImageResolveItemCount: (inFlight || showResult ? 1 : 0) + (showQueueCard ? 1 : 0),
  };
}
